import React, { useState, useEffect } from "react";
import styles from "./NuevoProductoModal.module.scss";
import { connect } from "react-redux";
import { openMainModal, closeMainModal } from "../../../../../store/actions";
import { ImagePlus } from "lucide-react";
import CustomSelect from "../../../../../pages/ProjectDocumentation/components/CustomSelect.tsx";
import PlacesAutocompleteModal from "./PlacesAutocompleteModal.tsx";
import ImageCarousel from "./ImageCarousel.tsx";
import StarterKitThumbnailSelector from "../../../../UI/FeedModal/StarterKitThumbnailSelector.tsx";
import StarterKitImagePicker from "../../../../UI/FeedModal/StarterKitImagePicker.tsx";

interface PortfolioFormValues {
  name: string;
  startDate: string;
  endDate: string;
  description: string;
  zone: string;
  type: string;
  priceUnit: string;
  price: string;
  currencyType: string;
}

type ImageItem = 
  | { type: 'new'; file: File }
  | { type: 'existing'; id: number; url: string };

interface NuevoProductoModalProps {
  formData: PortfolioFormValues;
  onFieldChange: (field: keyof PortfolioFormValues, value: string) => void;
  onImageChange: (newImages: File[]) => void;
  imageFiles: File[];
  errors?: Record<string, string>;
  existingImages?: Array<{ id: number; image: string }>;
  onDeleteExistingImage?: (imageId: number) => void;
  isEditing?: boolean;
  openMainModal?: (config: any) => void;
  closeMainModal?: () => void;
  dateError?: string;
}

const NuevoProductoModal: React.FC<NuevoProductoModalProps> = ({
  formData,
  onFieldChange,
  onImageChange,
  imageFiles,
  errors = {},
  existingImages = [],
  onDeleteExistingImage,
  isEditing = false,
  openMainModal,
  closeMainModal,
  dateError,
}) => {
  const { name, description, endDate, startDate, zone, type, priceUnit, price, currencyType } = formData;

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const combinedImages: ImageItem[] = [
    ...existingImages.map(img => ({ type: 'existing' as const, id: img.id, url: img.image })),
    ...imageFiles.map(file => ({ type: 'new' as const, file }))
  ];

  const totalImagesCount = existingImages.length + imageFiles.length;

  const [starterKitPickerOpen, setStarterKitPickerOpen] = useState(false);
  const [starterKitPickerMode, setStarterKitPickerMode] = useState<"single" | "multiple">("single");

  const openStarterKitPicker = (mode: "single" | "multiple") => {
    setStarterKitPickerMode(mode);
    setStarterKitPickerOpen(true);
  };

  const handleStarterKitSelect = (file: File) => {
    const nextCount = existingImages.length + imageFiles.length + 1;
    if (nextCount > 5) {
      if (openMainModal) {
        openMainModal({
          title: "No se pueden agregar más de 5 imágenes",
          body: "Por favor, elimine alguna de las imágenes existentes ¡Muchas gracias!",
          isSuccess: true,
        });
      }
      return;
    }

    onImageChange([...imageFiles, file]);
  };

  const handleStarterKitDeselect = (fileName: string) => {
    onImageChange(imageFiles.filter((f) => f.name !== fileName));
  };

  useEffect(() => {
    if (combinedImages.length === 0) {
      setCurrentImageIndex(0);
    } else if (currentImageIndex >= combinedImages.length) {
      setCurrentImageIndex(Math.max(0, combinedImages.length - 1));
    } else if (currentImageIndex < 0) {
      setCurrentImageIndex(0);
    }
  }, [combinedImages.length, currentImageIndex]);

  const prevImageFilesLengthRef = React.useRef(imageFiles.length);
  const prevExistingImagesLengthRef = React.useRef(existingImages.length);

  useEffect(() => {
    const prevTotal = prevExistingImagesLengthRef.current + prevImageFilesLengthRef.current;
    const currentTotal = existingImages.length + imageFiles.length;
    
    if (currentTotal > prevTotal && currentTotal > 0) {
      setCurrentImageIndex(currentTotal - 1);
    }
    
    prevImageFilesLengthRef.current = imageFiles.length;
    prevExistingImagesLengthRef.current = existingImages.length;
  }, [imageFiles.length, existingImages.length]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const updatedFiles = Array.from(files);

    const totalImageCount = existingImages.length + imageFiles.length;
    if (updatedFiles.length + totalImageCount > 5) {
      if (openMainModal) {
        openMainModal({
          title: "No se pueden agregar más de 5 imágenes",
          body: "Por favor, elimine alguna de las imágenes existentes ¡Muchas gracias!",
          isSuccess: true,
        });
      }
      event.target.value = '';
      return;
    }

    const newImages = [...imageFiles, ...updatedFiles];
    onImageChange(newImages);
    event.target.value = '';
  };

  const handleDeleteImage = (index: number) => {
    if (index < 0 || index >= combinedImages.length) return;
    
    const imageToDelete = combinedImages[index];
    
    if (imageToDelete.type === 'existing') {
      if (onDeleteExistingImage) {
        onDeleteExistingImage(imageToDelete.id);
      }
    } else {
      const newFilesIndex = index - existingImages.length;
      if (newFilesIndex >= 0 && newFilesIndex < imageFiles.length) {
        const imagesCopy = [...imageFiles];
        imagesCopy.splice(newFilesIndex, 1);
        onImageChange(imagesCopy);
      }
    }
    
    const remainingCount = combinedImages.length - 1;
    if (remainingCount === 0) {
      setCurrentImageIndex(0);
    } else if (currentImageIndex >= remainingCount) {
      setCurrentImageIndex(Math.max(0, remainingCount - 1));
    }
  };

  const handleAddImageFromCarousel = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleImageChange(event);
  };

  const typeOptions = [
    { value: "", label: "Producto/servicio" },
    { value: "producto", label: "Producto" },
    { value: "servicio", label: "Servicio" },
  ];

  const currencyOptions = [
    { value: "", label: "ARS/USD/EUR" },
    { value: "ARS", label: "ARS" },
    { value: "USD", label: "USD" },
    { value: "EUR", label: "EUR" },
  ];

  const priceUnitOptions = [
    { value: "", label: "Precio por" },
    { value: "unidad", label: "Unidad" },
    { value: "m", label: "Metro" },
    { value: "m2", label: "Metro cuadrado" },
    { value: "cm3", label: "Centimetro cúbico" },
    { value: "hora", label: "Hora" },
    { value: "trabajo completo", label: "Trabajo completo" },
  ];

  return (
    <div className={styles.infoGeneralContainer}>
      <input
        type="file"
        name="images"
        id="images"
        onChange={handleImageChange}
        style={{ display: "none" }}
        accept="image/*"
        multiple
      />
      <div className={`${errors.images ? styles.imageSectionHasError : ""}`} data-field="images">
        {combinedImages && combinedImages.length > 0 ? (
          <ImageCarousel
            images={combinedImages}
            currentIndex={Math.max(0, Math.min(currentImageIndex, combinedImages.length - 1))}
            onIndexChange={setCurrentImageIndex}
            onDeleteImage={handleDeleteImage}
            onAddImage={handleAddImageFromCarousel}
            onOpenStarterKit={() => openStarterKitPicker("single")}
            canOpenStarterKit={totalImagesCount < 5}
          />
        ) : (
          <div className={styles.emptyContainer}>
            <div className={styles.emptyContainer_choicesRow}>
              <span className={styles.emptyContainer_choicesText}>
                Elegí una de las que proponemos o usá el catálogo completo
              </span>
              <button type="button" className={styles.emptyContainer_catalogButton} onClick={() => openStarterKitPicker("multiple")}>
                Catálogo
              </button>
            </div>

            <StarterKitThumbnailSelector
              mode="multiple"
              maxImages={5}
              currentImagesCount={totalImagesCount}
              variant="inline"
              previewCount={9}
              showBorder={false}
              hideHeader={true}
              onSelect={handleStarterKitSelect}
            />

            <div className={styles.emptyContainer_or}>o</div>

            <label htmlFor="images" className={styles.emptyContainer_uploadButton}>
              <ImagePlus size={18} />
              <span>Subir imagen propia</span>
            </label>

            <span className={styles.emptyContainer_specsAfterButton}>JPG, PNG hasta 5MB</span>
          </div>
        )}
        {errors.images && <span className={styles.errorMessage}>{errors.images}</span>}
      </div>
      <div className={styles.isPortfolio}>
        <div className={styles.itemInfo}>
          <div className={`${styles.itemInfo_column} ${errors.name ? styles.hasError : ""}`} data-field="name">
            NOMBRE
            <input
              type="text"
              id="name"
              value={name}
              className={`${styles.itemInfo_input} ${errors.name ? styles.error : ""}`}
              placeholder="Por ejemplo: Instalación de A/A"
              onChange={(e) => onFieldChange("name", e.target.value)}
            />
            {errors.name && <span className={styles.errorMessage}>{errors.name}</span>}
          </div>
          <div className={`${styles.itemInfo_column} ${errors.type ? styles.hasError : ""}`} data-field="type">
            TIPO
            <CustomSelect
              value={type}
              onChange={(value) => onFieldChange("type", value)}
              options={typeOptions}
              placeholder="Producto/servicio"
              error={errors.type}
            />
          </div>
        </div>
        <div className={styles.itemInfo}>
          <div className={`${styles.itemInfo_column} ${errors.zone ? styles.hasError : ""}`} data-field="zone">
            UBICACIÓN
            <PlacesAutocompleteModal
              value={zone}
              onChange={(value) => onFieldChange("zone", value)}
              placeholder="Pilar, Buenos Aires"
            />
            {errors.zone && <span className={styles.errorMessage}>{errors.zone}</span>}
          </div>
          <div className={`${styles.itemInfo_column} ${errors.currencyType ? styles.hasError : ""}`} data-field="currencyType">
            MONEDA
            <CustomSelect
              value={currencyType}
              onChange={(value) => onFieldChange("currencyType", value)}
              options={currencyOptions}
              placeholder="ARS/USD/EUR"
              error={errors.currencyType}
            />
          </div>
        </div>
        <div className={styles.itemInfo}>
          <div className={`${styles.itemInfo_column} ${errors.priceUnit ? styles.hasError : ""}`} data-field="priceUnit">
            PRECIO
            <CustomSelect
              value={priceUnit}
              onChange={(value) => onFieldChange("priceUnit", value)}
              options={priceUnitOptions}
              placeholder="Precio por"
              error={errors.priceUnit}
            />
          </div>
          <div className={`${styles.itemInfo_column} ${errors.price ? styles.hasError : ""}`} data-field="price">
            VALOR
            <input
              type="number"
              id="price"
              value={price}
              className={`${styles.itemInfo_input} ${errors.price ? styles.error : ""}`}
              placeholder="Ej: 100"
              onChange={(e) => onFieldChange("price", e.target.value)}
            />
            {errors.price && <span className={styles.errorMessage}>{errors.price}</span>}
          </div>
        </div>
        <div className={styles.itemInfo}>
          <div className={`${styles.itemInfo_column} ${errors.startDate ? styles.hasError : ""}`} data-field="startDate">
            INICIO
            <input
              type="date"
              name="startDate"
              id="startDate"
              className={`${styles.itemInfo_input} ${(dateError || errors.startDate) ? styles.error : ""}`}
              value={startDate}
              onChange={(e) => onFieldChange("startDate", e.target.value)}
            />
            {errors.startDate && <span className={styles.errorMessage}>{errors.startDate}</span>}
          </div>
          <div className={`${styles.itemInfo_column} ${errors.endDate ? styles.hasError : ""}`} data-field="endDate">
            FIN
            <input
              type="date"
              name="endDate"
              id="endDate"
              className={`${styles.itemInfo_input} ${(dateError || errors.endDate) ? styles.error : ""}`}
              value={endDate}
              min={startDate}
              onChange={(e) => onFieldChange("endDate", e.target.value)}
            />
            {errors.endDate && <span className={styles.errorMessage}>{errors.endDate}</span>}
          </div>
        </div>
        {dateError && !errors.startDate && !errors.endDate && (
          <div className={styles.errorMessage}>
            {dateError}
          </div>
        )}
        <div className={styles.itemInfo}>
          <div className={`${styles.itemInfo_column} ${errors.description ? styles.hasError : ""}`} data-field="description">
            DESCRIPCIÓN
            <textarea
              value={description}
              id="description"
              name="description"
              className={`${styles.itemInfo_input} ${styles.itemInfo_area} ${errors.description ? styles.error : ""}`}
              onChange={(e) => onFieldChange("description", e.target.value)}
              placeholder="Ej: Instalación de 10 unidades de A/A en edificio de oficinas."
            />
            {errors.description && <span className={styles.errorMessage}>{errors.description}</span>}
          </div>
        </div>
      </div>
      <StarterKitImagePicker
        isOpen={starterKitPickerOpen}
        onClose={() => setStarterKitPickerOpen(false)}
        onSelect={handleStarterKitSelect}
        onDeselect={starterKitPickerMode === "multiple" ? handleStarterKitDeselect : undefined}
        mode={starterKitPickerMode}
        maxImages={5}
        currentImagesCount={existingImages.length + imageFiles.length}
        excludeFileNames={imageFiles.map((f) => f.name)}
      />
    </div>
  );
};

const ConnectedNuevoProductoModal = connect(null, {
  openMainModal,
  closeMainModal,
})(NuevoProductoModal) as React.FC<NuevoProductoModalProps>;

export default ConnectedNuevoProductoModal;
